import React from "react";
import { Node } from "@tiptap/core";
import { checkAndHandleMatrixCellCitation } from "../common/MarkDownRenderer";
import { getSourceMapForQid } from "../ReferenceWrapper/services/getSourceRefs";
import store from "@/redux/store";
import { setFileViewerData } from "@/redux/fileViewerSlice";

const getParentElementText = (e: React.MouseEvent<HTMLSpanElement>) => {
  const parent = (e.target as HTMLSpanElement)?.parentElement;
  if (!parent) return "";
  const parentNodeCopy = parent.cloneNode(true) as HTMLElement;
  const citationSpans = parentNodeCopy.querySelectorAll(".node-citation");
  citationSpans.forEach((span) => span.remove());

  return parentNodeCopy.textContent?.trim() || "";
};

// Custom Tiptap extension for citations
const CitationNode = Node.create({
  name: "citation",
  group: "inline",
  inline: true,
  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
      },
      qid: {
        default: null,
      },
      displaySrcNum: {
        default: 1,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "citation",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["citation", HTMLAttributes];
  },

  addNodeView() {
    return ({ node }) => {
      const { id, qid, displaySrcNum } = node.attrs;

      const dom = document.createElement("span");
      if (!displaySrcNum || displaySrcNum === "undefined") {
        return {
          dom,
        };
      }
      dom.className =
        "node-citation rounded-full mr-1 bg-gray-400 text-gray-100 text-[8px] p-0.5 px-1 cursor-pointer align-top h-fit";
      dom.dataset.qid = qid;
      dom.dataset.id = id;
      dom.style.marginLeft = "4px";
      dom.innerHTML = (displaySrcNum || "").toString();
      dom.onclick = async (e) => {
        const parentElementText = getParentElementText(
          e as unknown as React.MouseEvent<HTMLSpanElement>
        );

        const citationId = id?.replace(/^\[|\]$/g, "") || "";

        // Check if this citation references an RMS entity
        if (qid && citationId) {
          const sourceMap = await getSourceMapForQid(qid);
          const refs = sourceMap[`[${citationId}]`] || sourceMap[citationId];
          const ref = refs?.[0] as any;
          // Any citation that names an RMS record opens its preview — including
          // a structured data-source row that names one, which a cited
          // transaction line does (#11103). Tested on the id + kind pair rather
          // than on `rmsEntity`, because the pair is what the preview opens on
          // while the flag only says how the entry was harvested.
          if (ref?.publicId && ref?.kind) {
            window.dispatchEvent(
              new CustomEvent("openEntityPreview", {
                detail: {
                  entityKind: ref.kind,
                  entityId: ref.publicId,
                  factName: ref.factName,
                },
              })
            );
            return;
          }

          // Structured data source with nothing single to open (an aggregate, a
          // group bucket, a row count, a line linked to nothing): open the
          // source viewer on the values themselves. It shows the query only when
          // the source carried one. Rows are lazy-loaded by qid in the viewer.
          if (ref?.iodlSource) {
            store.dispatch(
              setFileViewerData({
                isOpen: true,
                fileType: "iodl-source",
                sql: ref.sql,
                sourceLabel: ref.sourceLabel ?? null,
                iodlColumns: ref.columns,
                rowCount: ref.rowCount,
                iodlReasoning: ref.reasoning,
                iodlQid: qid,
              })
            );
            return;
          }
        }

        // Check if this citation references a matrix cell (for matrix workspaces)
        if (qid && citationId) {
          const isMatrixCellCitation = await checkAndHandleMatrixCellCitation(
            citationId,
            qid,
            parentElementText
          );
          if (isMatrixCellCitation) {
            // Matrix cell citation was handled, don't open document panel
            return;
          }
        }

        // Default behavior: Dispatch window event to open document panel
        window.dispatchEvent(
          new CustomEvent("openDocumentPanel", {
            detail: {
              citationId,
              queryId: qid,
              parentElementText,
            },
          })
        );

        // Also call handleReferenceClick if available for backward compatibility
        if ((window as any).handleReferenceClick) {
          (window as any).handleReferenceClick(e, {
            qid,
            parentElementText,
            id,
          });
        }
      };

      return {
        dom,
      };
    };
  },
});

export default CitationNode;
